/**
 * UI Store (Zustand)
 * 
 * Global UI state management
 * - Modal state management
 * - Toast/notification state
 * - Offline status indicator
 * - Loading overlays
 * 
 * Requirements: 11.7, 12.1, 12.2
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// Types
export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

export interface Modal {
  id: string;
  title: string;
  content: React.ReactNode | string;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
}

interface UIState {
  // State
  isOffline: boolean;
  modals: Modal[];
  toasts: Toast[];
  loadingOverlay: boolean;
  loadingMessage: string | null;

  // Actions
  setOffline: (offline: boolean) => void;
  showModal: (modal: Omit<Modal, 'id'>) => string;
  hideModal: (id: string) => void;
  hideAllModals: () => void;
  showToast: (type: ToastType, message: string, duration?: number) => string;
  hideToast: (id: string) => void;
  showLoadingOverlay: (message?: string) => void;
  hideLoadingOverlay: () => void;
}

/**
 * Generate unique ID for modals and toasts
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * UI Store
 * 
 * Manages global UI state using Zustand
 */
export const useUIStore = create<UIState>()(
  devtools(
    (set, get) => ({
      // Initial state
      isOffline: !navigator.onLine,
      modals: [],
      toasts: [],
      loadingOverlay: false,
      loadingMessage: null,

      /**
       * Set offline status
       * Requirements: 11.7
       */
      setOffline: (offline: boolean) => {
        set({ isOffline: offline });

        // Show toast when going offline/online
        if (offline) {
          get().showToast('warning', 'You are offline. Some features may be unavailable.', 5000);
        } else {
          get().showToast('success', 'You are back online.', 3000);
        }
      },

      /**
       * Show a modal dialog
       */
      showModal: (modal: Omit<Modal, 'id'>) => {
        const id = generateId();
        const newModal: Modal = {
          id,
          ...modal,
        };

        set(state => ({
          modals: [...state.modals, newModal],
        }));

        return id;
      },

      /**
       * Hide a specific modal
       */
      hideModal: (id: string) => {
        set(state => ({
          modals: state.modals.filter(m => m.id !== id),
        }));
      },

      /**
       * Hide all modals
       */
      hideAllModals: () => {
        set({ modals: [] });
      },

      /**
       * Show a toast notification
       * Requirements: 12.1, 12.2
       */
      showToast: (type: ToastType, message: string, duration: number = 3000) => {
        const id = generateId();
        const newToast: Toast = {
          id,
          type,
          message,
          duration,
        };

        set(state => ({
          toasts: [...state.toasts, newToast],
        }));

        // Auto-hide toast after duration
        if (duration > 0) {
          setTimeout(() => {
            get().hideToast(id);
          }, duration);
        }

        return id;
      },

      /**
       * Hide a specific toast
       */
      hideToast: (id: string) => {
        set(state => ({
          toasts: state.toasts.filter(t => t.id !== id),
        }));
      },

      /**
       * Show loading overlay
       */
      showLoadingOverlay: (message?: string) => {
        set({
          loadingOverlay: true,
          loadingMessage: message || null,
        });
      },

      /**
       * Hide loading overlay
       */
      hideLoadingOverlay: () => {
        set({
          loadingOverlay: false,
          loadingMessage: null,
        });
      },
    }),
    { name: 'UIStore' }
  )
);

// Set up online/offline event listeners
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    useUIStore.getState().setOffline(false);
  });

  window.addEventListener('offline', () => {
    useUIStore.getState().setOffline(true);
  });
}

// Selectors for convenience
export const selectIsOffline = (state: UIState) => state.isOffline;
export const selectModals = (state: UIState) => state.modals;
export const selectToasts = (state: UIState) => state.toasts;
export const selectLoadingOverlay = (state: UIState) => state.loadingOverlay;
export const selectLoadingMessage = (state: UIState) => state.loadingMessage;

export default useUIStore;
